import { useRouter } from "next/router";
import { IZone, useGroups } from "@nju/data-access-portal-dashboard";
import { IMemberOrchestrators } from "@nju/data-access-portal-member";
import { useOfferModeWithFsecureWithoutFallback } from "@nju/data-access-portal-offers";
import {
  IMultiOrderOrchestrator,
  IOrchestrators,
  onboardingStarted,
} from "@nju/data-access-portal-orchestrator";
import { IMobileOrderProductsResponse } from "@nju/data-access-portal-product";
import { IEsimQRCode } from "@nju/data-access-portal-sim-card";
import { portalRoutes } from "@nju/routes";
import { Panel, Text } from "@nju/ui";
import { MobileDashboardMainPage } from "../MobileDashboardMainPage";
import { ConvergenceBanner } from "./ConvergenceBanner";
import { MobileBanner } from "./MobileBanner";
import { MultiDiscountBanner } from "./MultiDiscountBanner";
import { PostpaidBanner } from "./PostpaidBanner";

interface IMobileProps {
  handleSetSelectedNumber: (userId: string) => void;
  handleSetMultiDuoTrioContext: (flow: "multiOrder") => void;
  isLoading: boolean;
  selectedMobileProducts: IMobileOrderProductsResponse;
  multiOrderOrchestrator: IOrchestrators["multiOrder"] | undefined;
  selectedOrchestrators: IOrchestrators | IMemberOrchestrators;
  subState: IEsimQRCode["subState"] | undefined;
  zones: IZone[] | undefined;
}

export function Mobile({
  handleSetSelectedNumber,
  handleSetMultiDuoTrioContext,
  isLoading,
  selectedMobileProducts,
  selectedOrchestrators,
  multiOrderOrchestrator,
  subState,
  zones,
}: IMobileProps) {
  const mode = useOfferModeWithFsecureWithoutFallback();
  const mobile = selectedOrchestrators?.mobile;
  const groups = useGroups();
  const router = useRouter();
  const params = portalRoutes.mojeKonto.index.input(router.query).unwrap();
  const currentOfferCategory: IMultiOrderOrchestrator["offerCategory"] =
    mode === "postpaid" ? "MOBILE_POSTPAID" : "MOBILE";
  const hasGroupProduct = groups?.some((group) => {
    return group.members?.some((member) => {
      return (
        member.id === params?.userId &&
        member.primaryMsisdn &&
        (member.offerCategory === currentOfferCategory ||
          (mode === "mobile" && !member.offerCategory))
      );
    });
  });
  const isMobileOrchestratorReady =
    !mobile ||
    (mobile.offerCategory && mobile.offerCategory !== "MOBILE") ||
    !onboardingStarted(mobile.state);
  const isMultiOrderMobileOrchestratorReady =
    !multiOrderOrchestrator ||
    (multiOrderOrchestrator.offerCategory &&
      multiOrderOrchestrator.offerCategory !== "MOBILE") ||
    !onboardingStarted(multiOrderOrchestrator.state);
  const showMobileStarter =
    isMobileOrchestratorReady &&
    isMultiOrderMobileOrchestratorReady &&
    !hasGroupProduct;
  const isPostpaidOrchestratorReady =
    !mobile ||
    mobile.offerCategory !== "MOBILE_POSTPAID" ||
    !onboardingStarted(mobile.state);
  const isMultiOrderPostpaidOrchestratorReady =
    !multiOrderOrchestrator ||
    multiOrderOrchestrator.offerCategory !== "MOBILE_POSTPAID" ||
    !onboardingStarted(multiOrderOrchestrator.state);
  const showPostpaidStarter =
    isPostpaidOrchestratorReady &&
    isMultiOrderPostpaidOrchestratorReady &&
    !hasGroupProduct;

  if (mode === "postpaid" ? showPostpaidStarter : showMobileStarter) {
    return (
      <div className="flex flex-col gap-4">
        <Panel
          iconName="shopping-bag"
          title={`nie masz jeszcze usługi nju ${
            mode === "postpaid" ? "abonament" : "subskrypcja"
          }`}
        >
          <Text size="l">Sprawdź naszą ofertę.</Text>
        </Panel>
        <div className="flex flex-col gap-4 from-tablet:flex-row">
          {mode === "postpaid" ? (
            <PostpaidBanner />
          ) : (
            <>
              <MobileBanner />
              <ConvergenceBanner />
            </>
          )}
        </div>
        {mode !== "postpaid" && <MultiDiscountBanner />}
      </div>
    );
  }

  return (
    <MobileDashboardMainPage
      handleSetMultiDuoTrioContext={handleSetMultiDuoTrioContext}
      handleSetSelectedNumber={handleSetSelectedNumber}
      isLoading={isLoading}
      multiOrderOrchestrator={multiOrderOrchestrator}
      selectedMobileProducts={selectedMobileProducts}
      selectedOrchestrators={selectedOrchestrators}
      subState={subState}
      zones={zones}
    />
  );
}
